"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { Send, X, LogIn, ImagePlus, Smile, Loader2 } from "lucide-react"
import { useAuthContext } from "@/lib/auth-context"
import { getDisplayName } from "@/lib/avatar"
import { uploadFile } from "@/lib/api/upload"
import { compressImage } from "@/lib/image-compress"
import { CommunityAvatar } from "./community-avatar"
import { EmojiPicker } from "./emoji-picker"

/** 最大图片数量 */
const MAX_IMAGES = 5
/** 单张图片最大体积（MB） */
const IMAGE_MAX_SIZE_MB = 20

interface ImageItem {
  /** 本地预览 URL（blob URL） */
  preview: string
  /** 上传完成后的远程 URL；为空表示上传中 */
  url: string | null
  /** 上传中 */
  uploading: boolean
  /** 上传出错 */
  error: boolean
}

interface CommentInputProps {
  /** 发送评论，左右为文字内容和回复目标 */
  onSubmit: (content: string, parentId?: string) => Promise<void>
  /** 当前正在回复的评论信息，为 null 表示顶级评论 */
  replyTo?: { commentId: string; authorName: string } | null
  /** 取消回复 */
  onCancelReply?: () => void
  /** 是否正在提交 */
  submitting?: boolean
  /** 紧凑模式：用于内联回复，更小头像，无卡片背景 */
  compact?: boolean
  /** 自动聚焦 */
  autoFocus?: boolean
  /** 非激活状态：显示为灰色，点击后激活 */
  inactive?: boolean
  /** 输入框获得焦点时回调 */
  onFocus?: () => void
}

export function CommentInput({
  onSubmit,
  replyTo,
  onCancelReply,
  submitting = false,
  compact = false,
  autoFocus = false,
  inactive = false,
  onFocus,
}: CommentInputProps) {
  const { isLoggedIn, currentUser, avatarUrl, openLogin } = useAuthContext()
  const [content, setContent] = useState("")
  const [images, setImages] = useState<ImageItem[]>([])
  const [emojiOpen, setEmojiOpen] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const emojiTriggerRef = useRef<HTMLButtonElement>(null)

  // 切换回复目标时 / autoFocus 时聚焦输入框
  useEffect(() => {
    if ((replyTo || autoFocus) && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [replyTo, autoFocus])

  // 自动调整 textarea 高度
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [content])

  // 清理 blob URL
  useEffect(() => {
    return () => {
      images.forEach((img) => URL.revokeObjectURL(img.preview))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ── 图片上传 ── */

  const handleImageSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const remaining = MAX_IMAGES - images.length
    if (remaining <= 0) return

    const selected = Array.from(files).slice(0, remaining)

    // 过滤非图片 & 过大文件
    const valid = selected.filter((f) => {
      if (!f.type.startsWith("image/")) return false
      if (f.size > IMAGE_MAX_SIZE_MB * 1024 * 1024) return false
      return true
    })

    if (valid.length === 0) return

    // 创建本地预览项，用 previewUrl 作为唯一标识
    const newItems: (ImageItem & { _file: File })[] = valid.map((f) => ({
      preview: URL.createObjectURL(f),
      url: null,
      uploading: true,
      error: false,
      _file: f,
    }))

    setImages((prev) => [...prev, ...newItems.map(({ _file: _, ...rest }) => rest)])

    // 并行压缩 + 上传，用 previewUrl 定位对应项
    newItems.forEach(async ({ preview: previewUrl, _file: file }) => {
      try {
        const compressed = await compressImage(file)
        const url = await uploadFile(compressed)
        setImages((prev) =>
          prev.map((item) =>
            item.preview === previewUrl ? { ...item, url, uploading: false } : item,
          ),
        )
      } catch {
        setImages((prev) =>
          prev.map((item) =>
            item.preview === previewUrl ? { ...item, uploading: false, error: true } : item,
          ),
        )
      }
    })
  }, [images.length])

  /** 点击失败图片重新上传 */
  const retryUpload = useCallback(async (idx: number) => {
    const item = images[idx]
    if (!item || item.uploading || item.url) return
    // blob URL 已有，直接用 fetch 还原成 File 再上传
    setImages((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, uploading: true, error: false } : it)),
    )
    try {
      const blob = await fetch(item.preview).then((r) => r.blob())
      const file = new File([blob], `image-${idx}.jpg`, { type: blob.type || "image/jpeg" })
      const compressed = await compressImage(file)
      const url = await uploadFile(compressed)
      setImages((prev) =>
        prev.map((it, i) => (i === idx ? { ...it, url, uploading: false, error: false } : it)),
      )
    } catch {
      setImages((prev) =>
        prev.map((it, i) => (i === idx ? { ...it, uploading: false, error: true } : it)),
      )
    }
  }, [images])

  const removeImage = useCallback((idx: number) => {
    setImages((prev) => {
      const removed = prev[idx]
      if (removed) URL.revokeObjectURL(removed.preview)
      return prev.filter((_, i) => i !== idx)
    })
  }, [])

  /* ── Emoji ── */

  const handleEmojiSelect = useCallback((emoji: string) => {
    const el = textareaRef.current
    if (el) {
      const start = el.selectionStart
      const end = el.selectionEnd
      const newContent = content.slice(0, start) + emoji + content.slice(end)
      setContent(newContent)
      // 恢复光标位置
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = start + emoji.length
        el.focus()
      })
    } else {
      setContent((prev) => prev + emoji)
    }
  }, [content])

  /* ── 提交 ── */

  async function handleSubmit() {
    const trimmed = content.trim()
    const uploadedUrls = images.filter((img) => img.url).map((img) => img.url!)
    const hasUploading = images.some((img) => img.uploading)

    // 至少要有文字或图片，且不能有正在上传中的图片
    if ((!trimmed && uploadedUrls.length === 0) || submitting || hasUploading) return

    // 将图片以 <img> 标签嵌入 content
    const imgTags = uploadedUrls.map((url) => `<img src="${url}">`).join("")
    const finalContent = uploadedUrls.length > 0
      ? (trimmed ? `${trimmed}\n${imgTags}` : imgTags)
      : trimmed

    await onSubmit(finalContent, replyTo?.commentId)
    setContent("")
    setImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.preview))
      return []
    })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Ctrl/Cmd + Enter 发送
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault()
      handleSubmit()
    }
  }

  // ── 未登录态 ──
  if (!isLoggedIn) {
    return (
      <button
        type="button"
        onClick={openLogin}
        className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-sm text-muted-foreground hover:text-foreground transition-all duration-200"
        style={{
          background: 'color-mix(in oklch, oklch(var(--card)) 60%, transparent)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px dashed color-mix(in oklch, oklch(var(--border)) 50%, transparent)',
        }}
      >
        <LogIn className="h-4 w-4" />
        登录后参与评论
      </button>
    )
  }

  // ── 已登录态 ──
  const displayName = currentUser
    ? getDisplayName({
      username: currentUser.username,
      email: currentUser.email,
      phone: currentUser.phone,
    })
    : "用户"

  const hasContent = content.trim().length > 0 || images.some((img) => img.url)
  const hasUploading = images.some((img) => img.uploading)
  const canAddImage = images.length < MAX_IMAGES

  return (
    <div
      className={`transition-all duration-200 ${compact ? "py-2" : "rounded-2xl p-3"}`}
      style={compact ? undefined : {
        background: 'color-mix(in oklch, oklch(var(--card)) 70%, transparent)',
        backdropFilter: 'blur(16px) saturate(180%)',
        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        border: '1px solid color-mix(in oklch, oklch(var(--border)) 40%, transparent)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
      }}
    >
      <div className={`flex items-start ${compact ? "" : "gap-3"}`}>
        {/* 用户头像（内联模式不显示） */}
        {!compact && (
          <CommunityAvatar src={avatarUrl ?? undefined} name={displayName} sizeClass="w-8 h-8" className={`mt-1 shrink-0 ${inactive ? "opacity-50" : ""}`} />
        )}

        {/* 输入区域 */}
        <div className="flex-1 min-w-0">
          {/* 回复提示（内联模式不显示） */}
          {!compact && replyTo && (
            <div className="flex items-center gap-1.5 mb-2 text-xs text-muted-foreground">
              <span>
                回复 <span className="text-foreground font-medium">{replyTo.authorName}</span>
              </span>
              {onCancelReply && (
                <button
                  type="button"
                  onClick={onCancelReply}
                  className="inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-foreground/10 transition-colors"
                  aria-label="取消回复"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          )}

          {/* 输入框 */}
          <div
            className="rounded-xl transition-all duration-200"
            style={{
              border: inactive
                ? '1px solid color-mix(in oklch, oklch(var(--border)) 25%, transparent)'
                : '1px solid color-mix(in oklch, oklch(var(--border)) 50%, transparent)',
              background: inactive
                ? 'color-mix(in oklch, oklch(var(--background)) 40%, transparent)'
                : 'color-mix(in oklch, oklch(var(--background)) 80%, transparent)',
            }}
          >
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={onFocus}
              placeholder={replyTo ? `回复 ${replyTo.authorName}…` : "写下你的评论…"}
              rows={1}
              disabled={submitting}
              className={`w-full resize-none bg-transparent text-sm placeholder:text-muted-foreground/50 py-2.5 px-3.5 outline-none disabled:opacity-50 leading-relaxed ${inactive ? "text-muted-foreground/40 cursor-pointer" : "text-foreground"
                }`}
            />

            {/* 图片预览区 */}
            {images.length > 0 && (
              <div className="flex flex-wrap gap-2 px-3 pb-2.5">
                {images.map((img, idx) => (
                  <div
                    key={img.preview}
                    className="relative group w-16 h-16 rounded-lg overflow-hidden border border-border/30"
                  >
                    <img
                      src={img.preview}
                      alt={`附图 ${idx + 1}`}
                      className={`w-full h-full object-cover ${img.uploading ? "opacity-50" : ""} ${img.error ? "opacity-30" : ""}`}
                    />
                    {/* 上传中遮罩 */}
                    {img.uploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <Loader2 className="h-4 w-4 text-white animate-spin" />
                      </div>
                    )}
                    {/* 上传失败标记 — 点击重试 */}
                    {img.error && (
                      <button
                        type="button"
                        onClick={() => retryUpload(idx)}
                        className="absolute inset-0 flex flex-col items-center justify-center bg-red-500/20 hover:bg-red-500/30 transition-colors"
                        title="点击重试"
                      >
                        <span className="text-micro text-red-400 font-medium">失败</span>
                        <span className="text-micro text-red-400/70">重试</span>
                      </button>
                    )}
                    {/* 删除按钮 */}
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="移除图片"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 底部操作栏 */}
          <div className="flex items-center justify-between gap-2 mt-2">
            {/* 左侧工具按钮 */}
            <div className="flex items-center gap-1">
              {/* 上传图片 */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                aria-label="选择图片"
                onChange={(e) => {
                  handleImageSelect(e.target.files)
                  e.target.value = ""
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canAddImage || submitting}
                className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs transition-colors ${canAddImage
                  ? "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.06]"
                  : "text-muted-foreground/30 cursor-not-allowed"
                  }`}
                title={canAddImage ? `添加图片（还可添加 ${MAX_IMAGES - images.length} 张）` : "最多 5 张图片"}
              >
                <ImagePlus className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {images.length > 0 ? `${images.length}/${MAX_IMAGES}` : "图片"}
                </span>
              </button>

              {/* Emoji 选择器 */}
              <div className="relative">
                <button
                  ref={emojiTriggerRef}
                  type="button"
                  onClick={() => setEmojiOpen((v) => !v)}
                  disabled={submitting}
                  className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-50 ${emojiOpen
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.06]"
                    }`}
                  title="插入表情"
                >
                  <Smile className="h-4 w-4" />
                  <span className="hidden sm:inline">表情</span>
                </button>

                <EmojiPicker
                  open={emojiOpen}
                  onClose={() => setEmojiOpen(false)}
                  onSelect={handleEmojiSelect}
                  triggerRef={emojiTriggerRef}
                />
              </div>

              <span className="text-micro text-muted-foreground/40 ml-1">
                ⌘+Enter 发送
              </span>
            </div>

            {/* 发送按钮 */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!hasContent || submitting || hasUploading}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${hasContent && !hasUploading
                ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm shadow-primary/20"
                : "bg-foreground/[0.06] text-muted-foreground/40 cursor-not-allowed"
                }`}
            >
              <Send className={`h-3 w-3 ${submitting ? "animate-pulse" : ""}`} />
              {hasUploading ? "上传中…" : "发送"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
